import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { bridgeDownloadUrl, isBridgeAlive, printTsplViaBridge } from "@/lib/api";

// Module-level cache so a page-switch doesn't re-ping the bridge every render.
// The bridge lives on the operator's PC — its state changes rarely.
let _cached = null;   // null = unknown, true/false = last known
let _lastCheck = 0;
const TTL_MS = 15_000;

/**
 * Shared print-bridge state + one-click print helper.
 *
 * Usage:
 *   const { bridgeAlive, print } = usePrintBridge();
 *   <PrintButton tspl={coilLabelTsplUrl(id)} pdf={singleLabelPdfUrl(id)} label="RM label" />
 *
 * The hook pings the bridge lazily (once per 15s across the app) so many
 * PrintButtons on the same page don't hammer 127.0.0.1.
 */
export const usePrintBridge = () => {
  const [bridgeAlive, setBridgeAlive] = useState(_cached);

  useEffect(() => {
    const now = Date.now();
    if (_cached !== null && now - _lastCheck < TTL_MS) {
      setBridgeAlive(_cached);
      return;
    }
    let cancelled = false;
    isBridgeAlive().then((ok) => {
      if (cancelled) return;
      _cached = ok;
      _lastCheck = Date.now();
      setBridgeAlive(ok);
    });
    return () => { cancelled = true; };
  }, []);

  const print = useCallback(async (tsplUrl, opts = {}) => {
    const { label = "labels", pdfFallbackUrl = null } = opts;
    // LAN relay unreachable? Fall back to the PDF (which the user can print
    // from any regular office printer while the relay is offline).
    if (bridgeAlive === false) {
      if (pdfFallbackUrl) {
        window.open(pdfFallbackUrl, "_blank", "noreferrer");
        toast.info(`Opened ${label} PDF — LAN print relay is not running.`, {
          action: { label: "Setup relay", onClick: () => window.open(bridgeDownloadUrl(), "_blank") },
        });
      } else {
        toast.error(`LAN print relay not running on any office PC. Install the one-time relay to enable one-click LAN printing.`, {
          action: { label: "Setup relay", onClick: () => window.open(bridgeDownloadUrl(), "_blank") },
          duration: 9000,
        });
      }
      return false;
    }
    const t = toast.loading(`Sending ${label} to LAN printer…`);
    try {
      await printTsplViaBridge(tsplUrl);
      toast.dismiss(t);
      toast.success(`Printed ${label}`);
      _cached = true;
      setBridgeAlive(true);
      return true;
    } catch (e) {
      toast.dismiss(t);
      _cached = false;
      setBridgeAlive(false);
      if (pdfFallbackUrl) {
        window.open(pdfFallbackUrl, "_blank", "noreferrer");
        toast.info(`LAN relay unreachable — opened ${label} PDF instead.`, {
          action: { label: "Setup relay", onClick: () => window.open(bridgeDownloadUrl(), "_blank") },
        });
      } else {
        toast.error(`Print failed: ${e?.message || "LAN relay unreachable"}`, {
          action: { label: "Setup relay", onClick: () => window.open(bridgeDownloadUrl(), "_blank") },
        });
      }
      return false;
    }
  }, [bridgeAlive]);

  return { bridgeAlive, print };
};
